///<reference path="Types.ts"/>
import * as React from 'react';

import {FileComponent} from "./Component";
import {FolderComponent} from "./Component";
import {RecursiveFolderTree} from "./Component";

import {Panel} from '../../lib/sbhtml/panel'
import PopoutWindow = require("react-popout");
import {CommandCaller} from "../../lib/commands";
import {PromiseResolver} from "../../lib/types";
import {
	HasAdminMemoryParams,
	FolderHierarchy,
	MemoryFile,
	DeleteCallbackMaybe,
	SelectCallbackMaybe,
	MemoryFolder,
	DeleteCallback,
	SelectCallback,
	HasFilesFolders,
	MemoryParams,
	FolderHierarchyRendererProps
} from "./Types";

interface AdminWidgetProps extends HasAdminMemoryParams {
	fieldName:string;
}

interface AdminWidgetState {
	showSelectionDialog:boolean;
}

abstract class AdminWidget<P extends AdminWidgetProps, S extends AdminWidgetState> extends React.Component<P, S> {

	protected abstract buttonLabel():string;
	protected abstract selectionDialog():JSX.Element;

	protected fileSelected(id:number, memoryInstance:string):void {};
	protected folderSelected(id:number, memoryInstance:string):void {};

	render() {
		return <div className="memory-module selected-items">
			<SelectButton
				buttonLabel={this.buttonLabel()}
				memoryPageUrl={this.props.memoryParams.location}
				windowHeight={this.props.memoryParams.windowHeight}
				windowWidth={this.props.memoryParams.windowWidth}
				openDialogCallback={() => this.onDialogOpen()}/>
			{this.renderData()}
			{this.state.showSelectionDialog ? this.selectionDialog() : null}
		</div>
	}

	protected abstract renderData():JSX.Element;

	protected onDialogClose() {
		this.setState((prevState) => {
			prevState.showSelectionDialog = false;
			return prevState;
		});
	}

	private onDialogOpen() {
		this.setState((prevState) => {
			prevState.showSelectionDialog = true;
			return prevState;
		});
	}
}

interface FileOrFolderWidgetProps<T extends MemoryFile> extends AdminWidgetProps {
	deleteCallback?:DeleteCallbackMaybe
	selectedCallback?:SelectCallbackMaybe
	selected:T|undefined
	buttonLabel?:string
}

interface AdminFileOrFolderWidgetState<T extends MemoryFile> extends AdminWidgetState {
	selected:T|null|undefined
}

abstract class FileOrFolderWidget<T extends MemoryFile> extends AdminWidget<FileOrFolderWidgetProps<T>, AdminFileOrFolderWidgetState<T>> {

	constructor(props?:FileOrFolderWidgetProps<T>, context?:any) {
		super(props, context);
		this.state = {
			selected: this.props.selected,
			showSelectionDialog: false
		};
	}

	renderData() {
		const value = this.state.selected ? this.state.selected.id : 0;
		return <div>
			<input type="hidden" name={this.props.fieldName} value={value.toString()} />
			{this.state.selected == null ? null : this.renderSelectedItem(this.state.selected)}
		</div>
	}

	protected abstract renderSelectedItem(item: T):JSX.Element

	protected selected(id:number, memoryInstance:string):void {
		if (this.props.selectedCallback == null || !this.props.selectedCallback(id, memoryInstance)) {
			(new CommandCaller(this.props.memoryParams.ajaxPageUrl)
				.runGet(this.command(), [id]) as Promise<T[]>)
				.then((items:T[]) => {
					if (items != null && items.length > 0) {
						this.setState({selected: items[0], showSelectionDialog: false})
					}
				})
		}
	}

	protected deleted(id:number):boolean {
		if (this.props.deleteCallback == null || !this.props.deleteCallback(id)) {
			this.setState({selected: null, showSelectionDialog: false});
		}
		return true;
	}

	protected abstract command():string;
}

export class FileWidget extends FileOrFolderWidget<MemoryFile> {

	protected command():string {
		return 'get file details';
	}

	protected buttonLabel():string {
		return this.props.buttonLabel == null ? 'Link a File' : this.props.buttonLabel;
	}

	protected renderSelectedItem(selected: MemoryFile):JSX.Element {
		return <FileComponent
			file={selected}
			deleteIconPath={this.props.memoryParams.deleteIconPath}
			deleteCallback={(id) => { return this.deleted(id) }} />
	}

	//noinspection JSMethodCanBeStatic,JSUnusedGlobalSymbols
	protected selectionDialog():JSX.Element {
		return <FileSelectionDialog
			memoryPageUrl={this.props.memoryParams.ajaxPageUrl}
			closeDialogCallback={() => this.onDialogClose()}
			fileSelectedCallback={(id, memoryInstance) => { return this.selected(id, memoryInstance)}}/>
	}

}

export class FolderWidget extends FileOrFolderWidget<MemoryFolder> {

	protected command():string {
		return 'get folder details';
	}

	protected buttonLabel():string {
		return this.props.buttonLabel == null ? 'Link a Folder' : this.props.buttonLabel;
	}

	protected renderSelectedItem(selected: MemoryFolder):JSX.Element {
		return <FolderComponent
			file={selected}
			deleteIconPath={this.props.memoryParams.deleteIconPath}
			deleteCallback={(id) => { return this.deleted(id) }} />
	}

	//noinspection JSMethodCanBeStatic,JSUnusedGlobalSymbols
	protected selectionDialog():JSX.Element {
		return <FolderSelectionDialog
			memoryPageUrl={this.props.memoryParams.ajaxPageUrl}
			closeDialogCallback={() => this.onDialogClose()}
			categorySelectedCallback={(id, memoryInstance) => { return this.selected(id, memoryInstance)}}/>
	}

}

/**
 * These callbacks are optional because a client using this API may choose to not handle any of the
 * selections/deletions. In such a case, the widget behaves like a HTML <input> widget and ensures it
 * sets the relevant state so that when a form is posted, it will post the selected files/folders.
 */
interface FilesFoldersCallbacks {
	folderDeletedCallback?:DeleteCallbackMaybe;
	folderSelectedCallback?:SelectCallbackMaybe;
	fileSelectedCallback?:SelectCallbackMaybe;
	fileDeletedCallback?:DeleteCallbackMaybe;
}

interface FilesFoldersWidgetState extends AdminWidgetState {
	files:MemoryFile[];
	folders:FolderHierarchy[];
	showSelectionDialog:boolean;
}

interface FilesFoldersWidgetProps extends AdminWidgetProps, FilesFoldersCallbacks, HasFilesFolders {}

abstract class SelectedItems<T> {

	constructor(
		private items:T[],
		private memoryParams:MemoryParams,
		private selectedCallback:SelectCallbackMaybe,
		private deletedCallback:DeleteCallbackMaybe
	) {}

	public has(id:number):boolean {
		return this.items != null && this.items.findIndex((item) => this.getId(item) == id) != -1;
	}

	// If the UI must be updated in response to selecting the specified item, then the promise will be resolved.
	public select(id:number, memoryInstance:string):Promise<{}> {
		return new Promise((resolve:PromiseResolver<{}>) => {
			if (this.has(id)) {
				resolve({});
				return
			}

			if (this.selectedCallback == null || !this.selectedCallback(id, memoryInstance)) {
				this.loadData(id).then((items) => {
					if (items != null && items.length > 0) {
						this.items.push(items[0]);
						resolve({})
					}
				})
			}
		})
	}

	protected commands() {
		return new CommandCaller(this.memoryParams.ajaxPageUrl)
	}

	// If the UI must be updated in response to selecting the specified item, then the promise will be resolved.
	public deleted(id:number):Promise<{}> {
		return new Promise((resolve:PromiseResolver<{}>) => {
			if (this.deletedCallback == null || !this.deletedCallback(id)) {
				const index = this.items.findIndex((file) => this.getId(file) == id);
				this.items.splice(index, 1)
				resolve({})
			}
		})
	}

	protected abstract getId(fromItem:T):number;

	protected abstract loadData(id:number):Promise<T[]>;

	public getItems() {return this.items;}
}

class SelectedFiles extends SelectedItems<MemoryFile> {
	protected getId(fromItem:MemoryFile):number { return fromItem.id; }
	protected loadData(id:number):Promise<MemoryFile[]> { return this.commands().runGet('get file details', [id]) as Promise<MemoryFile[]>; }
}

class SelectedFolders extends SelectedItems<FolderHierarchy> {
	protected getId(fromItem:FolderHierarchy):number { return fromItem.folderId; }
	protected loadData(id:number):Promise<FolderHierarchy[]> { return this.commands().runGet('get folder hierarchy details', [id]) as Promise<FolderHierarchy[]>; }
}

export class FilesFoldersWidget extends AdminWidget<FilesFoldersWidgetProps, FilesFoldersWidgetState> {

	private files:SelectedFiles;
	private folders:SelectedFolders;

	constructor(props:FilesFoldersWidgetProps, context?:any) {
		super(props, context);
		this.files   = new SelectedFiles(props.files, props.memoryParams, props.fileSelectedCallback, props.fileDeletedCallback)
		this.folders = new SelectedFolders(props.folders, props.memoryParams, props.folderSelectedCallback, props.folderDeletedCallback)
		this.state   = {
			files : this.props.files,
			folders: this.props.folders,
			showSelectionDialog: false
		}
	}

	protected selectionDialog():JSX.Element {
		return <FileFolderSelectionDialog
			memoryPageUrl={this.props.memoryParams.ajaxPageUrl}
			closeDialogCallback={() => this.onDialogClose()}
			fileSelectedCallback={(id, memoryInstance) => { return this.fileSelected(id, memoryInstance)}}
			categorySelectedCallback={(id:number, memoryInstance:string) => { return this.folderSelected(id, memoryInstance)}}/>
	}

	protected buttonLabel():string { return 'Link a File or Folder' }

	protected fileSelected(id:number, memoryInstance:string):void {
		this.files.select(id, memoryInstance).then(() => this.refreshState());
	}

	//noinspection JSUnusedGlobalSymbols
	protected folderSelected(id:number, memoryInstance:string):void {
		this.folders.select(id, memoryInstance).then(() => this.refreshState());
	}

	private refreshState() {
		this.setState({
			files: this.files.getItems(),
			folders: this.folders.getItems(),
			showSelectionDialog: this.state.showSelectionDialog
		})
	}

	protected hiddenInputsFor(fieldName:string, ids:number[]) {
		return ids.map((id) => {
			const inputName = `${this.props.fieldName}[${fieldName}][]`;
			return <input key={`input-for-${fieldName}-${id}`} type="hidden" name={inputName} value={id.toString()} />;
		});
	}

	renderData() {
		return <div>
			{this.hiddenInputsFor('fileIds', this.state.files.map((file) => { return file.id; }))}
			{this.hiddenInputsFor('folderIds', this.state.folders.map((folder) => { return folder.folderId; }))}
			<FileList
				files={this.state.files}
				deleteIconPath={this.props.memoryParams.deleteIconPath}
				deleteCallback={(id) => this.files.deleted(id).then(() => this.refreshState()) } />
			<FolderHierarchyList
				folders={this.state.folders}
				deleteIconPath={this.props.memoryParams.deleteIconPath}
				directoryIconPath={this.props.memoryParams.directoryIconPath}
				deleteCallback={(id) => this.folders.deleted(id).then(() => this.refreshState()) } />
		</div>
	}
}

interface FileListProps {
	files:MemoryFile[]
	deleteIconPath:string;
	deleteCallback:DeleteCallback;
}

class FileList extends React.Component<FileListProps, {}> {
	render() {
		if (this.props.files == null || this.props.files.length === 0) {
			return null;
		}

		return <Panel title="Individual Files">
			<ul className="file-list item-list">
				{this.props.files.map((file) => {
					return (
						<FileComponent
							key={`memory-file-${file.id}`}
							file={file}
							deleteIconPath={this.props.deleteIconPath}
							deleteCallback={(id) => this.props.deleteCallback(id)} />
					);
				})}
			</ul>
		</Panel>
	}
}

interface AdminFolderHierarchyRendererProps extends FolderHierarchyRendererProps {
	deleteIconPath:string;
	deleteCallback:DeleteCallback;
}

class FolderHierarchyList extends React.Component<AdminFolderHierarchyRendererProps, {}> {
	render() {
		if (this.props.folders == null || this.props.folders.length === 0) {
			return null;
		}

		return <ul className="category-list">
			{this.props.folders.map((folder) => {
				return (
					<CategoryDisplay
						key={`category-${folder.folderId}`}
						category={folder}
						deleteIconPath={this.props.deleteIconPath}
						directoryIconPath={this.props.directoryIconPath}
						deleteCallback={(id) => this.props.deleteCallback(id)}/>
				);
			})}
		</ul>
	}
}

interface CategoryDisplayProps extends React.Props<CategoryDisplay> {
	category:FolderHierarchy;
	deleteIconPath?:string;
	directoryIconPath:string;
	deleteCallback?:DeleteCallback;
}

class CategoryDisplay extends React.Component<CategoryDisplayProps, {}> {
	render() {
		const {deleteCallback} = this.props
		const onDelete = deleteCallback === undefined ? undefined :
			() => deleteCallback(this.props.category.folderId);

		return <Panel
			title={this.props.category.name}
			iconPath={this.props.directoryIconPath}
			rightIconPath={this.props.deleteIconPath}
			rightIconClick={onDelete}>

			<RecursiveFolderTree
				parentId={this.props.category.folderId}
				directoryIconPath={this.props.directoryIconPath}
				allPotentialChildren={this.props.category.hierarchy} />
		</Panel>
	}
}

interface SelectButtonProps {
	memoryPageUrl:string;
	buttonLabel:string;
	windowWidth:number;
	windowHeight:number;
	openDialogCallback:() => void;
}

class SelectButton extends React.Component<SelectButtonProps, {}> {
	render() {
		return <div>
			<input type="hidden" name="memory_category_selected" id="memory_category_selected" value=""/>
			<input type="hidden" name="memory_file_selected" id="memory_file_selected" value=""/>
			<input type="hidden" name="memory_instance" id="memory_instance" value=""/>
			<input type="button" name="repository" value={this.props.buttonLabel} className="submit" onClick={this.props.openDialogCallback}/>
		</div>
	}
}

interface SelectionDialogProps {
	memoryPageUrl:string;
	closeDialogCallback:() => void;
	categorySelectedCallback?:SelectCallback; //todo i think the hierarchy here is wrong
	fileSelectedCallback?:SelectCallback; //todo this is only defined some times, and that too, but we aren't telling everyone when and why!
}

abstract class SelectionDialog extends React.Component<SelectionDialogProps, {}> {

	constructor(props: SelectionDialogProps, context: any) {
		super(props, context);
		this.cancelClick = () => {
			const ref = this.windowRef
			ref && ref.closeWindow()
		}
	}

	private windowRef:PopoutWindow | undefined;
	private cancelClick: () => void

	protected abstract canSelectFolders():boolean;
	protected abstract canSelectFiles():boolean;

	componentWillMount() {
		window.addEventListener("message", this.onMessage);
	}

	componentWillUnmount() {
		window.removeEventListener("message", this.onMessage);
	}

	private command():string {
		if (this.canSelectFiles() && !this.canSelectFolders()) {
			return 'memory_select_file'
		} else if (!this.canSelectFiles() && this.canSelectFolders()) {
			return 'memory_select_folder'
		} else {
			return 'category_select'
		}
	}

	render() {
		const url = `${this.props.memoryPageUrl}/command/${this.command()}`
		return <PopoutWindow
			ref={(ref:PopoutWindow) => this.windowRef = ref}
			title="Select a document or folder"
			url={url}
			onClosing={() => this.props.closeDialogCallback()}>
			<div style={{padding: '2em'}}>
				<div>Loading list of files/folders...</div>
				<a href="#" onClick={this.cancelClick}>Cancel</a>
			</div>
		</PopoutWindow>
	}

	private onMessage = (event:MessageEvent) => {
		if (event.data.hasOwnProperty('eventType')) {
			if (event.data.eventType == "categorySelected") {
				this.props.categorySelectedCallback!(parseInt(event.data.folderId), event.data.memoryInstance);
			} else if (event.data.eventType == 'fileSelected') {
				this.props.fileSelectedCallback!(parseInt(event.data.fileId), event.data.memoryInstance);
			}
			const ref = this.windowRef
			ref && ref.closeWindow();
		}
	};
}

class FileSelectionDialog extends SelectionDialog {

	protected canSelectFolders():boolean {
		return false;
	}

	protected canSelectFiles():boolean {
		return true;
	}

}

class FolderSelectionDialog extends SelectionDialog {

	protected canSelectFolders():boolean {
		return true;
	}

	protected canSelectFiles():boolean {
		return false;
	}

}

class FileFolderSelectionDialog extends SelectionDialog {

	protected canSelectFolders():boolean {
		return true;
	}

	protected canSelectFiles():boolean {
		return true;
	}

}



// WEBPACK FOOTER //
// ./src/memory/component/Admin.tsx